/**
 * ChannelMatrixTable · Module B - chartType=table 时上方展示的"游戏 × 渠道"矩阵表
 *
 * Figma 设计：3270-41082
 *
 * 与 ChannelRankingTable 区别：
 *   - ChannelRankingTable：行=游戏、列=5 个 metrics（始终在下方展示，sum 接口）
 *   - ChannelMatrixTable（本组件）：行=游戏、列=各渠道（chartType=table 时上方展示，dimension_summary 接口）
 *
 * 数据源（来自 useChannelSummary）：
 *   - list:         IChannelChartBarItem[] · 当前周期值（按 channel 分桶）
 *   - linkMap:      game_id → channel_id → { value, oldValue, qoq } · 环比映射
 *   - linkTime:     { newTime, oldTime } · hover 上期对比气泡用
 *   - dimensionMax: channel_id → max · 每列进度条按列基准（来自接口 dimension_max；缺省时前端按列兜底）
 *
 * 列：
 *   - Game 列（icon + 名称，固定左）
 *   - 每个渠道一列（channelMap 映射名称 + channelInfo 取 icon 作为表头）
 *     · cell：ProgressColumn（数值 + 环比箭头 + 进度条 + Hover 上期对比气泡）
 *
 * - 前端分页（默认 10/页）
 * - 前端排序（任意渠道列；默认按首列 desc）
 * - 数值按当前 metric 的 yFormatter 决定：reviews / percent
 * - 进度条基准（totalValue）：
 *   · 百分比 metric → 1（即 100%；bar 宽度 == value 直观映射）
 *   · 绝对值 metric → dimensionMax[cid]（按渠道列峰值对比）
 *   接口 `dimension_max` 对 percent metric 是 raw_value 单位（计数），不直接可用作百分比 totalValue。
 */
import React, { useMemo, useState } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import {
  channelInfo,
  ChannelIconType,
} from '@op/components/ChannelLabel';
import { DBO_TM, DBO_V } from '@op/utils/format';
import ProgressColumn from '@op/pages/Overview/CompareKeyMetrics/component/ProgressColumn';
import { SortBy } from 'tea-component/lib/table/addons';
import useIntls from '@opm/hooks/useIntls';
import RenderImg from '@op/components/RenderImg';
import { DefaultImg } from '@op/constants/defaultImg';
import { IChannelChartBarItem } from '../../types/channel';
import { TGameMap } from '../../hooks/useGameMap';
import { TChannelMap } from '../../hooks/useChannelMap';
import { TMetric } from '../../types/common';
import { METRIC_METAS } from '../../constants/metrics';
import {
  IChannelLinkTime,
  TChannelLinkMap,
} from '../../hooks/useChannelCompare';
import TextBubbles from '@home/components/TextBubbles';

export interface IChannelMatrixTableProps {
  /** useChannelSummary.barList 的输出（行=渠道，每行 games[]） */
  list: IChannelChartBarItem[];
  /** game_id → channel_id → { value, oldValue, qoq } 环比映射（cell 直接查） */
  linkMap: TChannelLinkMap;
  /** 环比时间窗口（hover 上期对比气泡用） */
  linkTime: IChannelLinkTime;
  /** 各渠道列的进度条基准（channel_id → max；接口 dimension_max；缺省时由 hook 前端按列兜底） */
  dimensionMax: Record<string, number>;
  /** 当前选中指标（决定数值格式：reviews / percent） */
  metric: TMetric;
  gameMap: TGameMap;
  /** 渠道元信息映射（id → meta，用于渠道名称翻译） */
  channelMap: TChannelMap;
  loading?: boolean;
  emptyTitleText?: string;
  height?: number;
  /**
   * 排序状态（受控；父级提升以便下载时复用）
   * `by` = channel_id；空字符串表示未排序（默认数据顺序）
   */
  sort: SortBy<any>;
  onSortChange: (next: SortBy<any>) => void;
}

interface IPageInfoState {
  page: number;
  page_size: number;
}

const DEFAULT_PAGE_INFO: IPageInfoState = { page: 1, page_size: 50 };

/** 转置后的行结构 */
interface IGameRow {
  game_id: string;
  /** channel_id → value 映射（当前周期值） */
  byChannel: Record<string, number>;
}

/**
 * 把 barList 转置为「行=游戏」的结构
 * - 同时收集 channel_id 顺序（按 barList 中首次出现）
 */
function transpose(list: IChannelChartBarItem[]): {
  rows: IGameRow[];
  channelIds: string[];
} {
  const channelIds: string[] = list.map(c => c.channel_id);
  /** game_id → row 映射，保持插入顺序 */
  const map = new Map<string, IGameRow>();
  list.forEach(c => {
    c.games.forEach(g => {
      const v = Number.isFinite(g.value) ? g.value : 0;
      let row = map.get(g.game_id);
      if (!row) {
        row = { game_id: g.game_id, byChannel: {} };
        map.set(g.game_id, row);
      }
      row.byChannel[c.channel_id] = v;
    });
  });
  return { rows: Array.from(map.values()), channelIds };
}

export default function ChannelMatrixTable({
  list,
  linkMap,
  linkTime,
  dimensionMax,
  metric,
  gameMap,
  channelMap,
  loading,
  emptyTitleText,
  height,
  sort,
  onSortChange,
}: IChannelMatrixTableProps) {
  const { intl } = useIntls();

  const empty = useMemo(() => {
    return (
      !list?.length ||
      list.every(item => !item.games?.length || item.games.every(g => !g.value))
    );
  }, [list]);

  /** 转置数据 */
  const { rows, channelIds } = useMemo(() => transpose(list), [list]);

  const [pageInfo, setPageInfo] = useState<IPageInfoState>(DEFAULT_PAGE_INFO);

  const labels = useMemo(
    () => ({
      game: intl('Game'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl],
  );

  /** 当前 metric 是否百分比 */
  const isPercent = METRIC_METAS[metric]?.yFormatter === 'percent';

  /** ProgressColumn 数值格式化器（百分比 metric 0~1 → "xx.xx%"） */
  const formateValue = useMemo(
    () =>
      isPercent
        ? (v: number | string) => DBO_V.percentageFormat(Number(v) * 100)
        : (v: number | string) => DBO_V.reviews(Number(v)),
    [isPercent],
  );

  /** 排序 */
  const sortedRows = useMemo(() => {
    const { by, order } = sort;
    if (!by) return rows;
    const dir = order === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a.byChannel[by] ?? 0;
      const bv = b.byChannel[by] ?? 0;
      return (av - bv) * dir;
    });
  }, [rows, sort]);

  /** 当前页 */
  const pagedRows = useMemo(() => {
    const start = (pageInfo.page - 1) * pageInfo.page_size;
    return sortedRows.slice(start, start + pageInfo.page_size);
  }, [sortedRows, pageInfo]);

  /** Game 单元格 */
  const renderGameCell = (row: IGameRow) => {
    const game = gameMap[row.game_id];
    return (
      <div className="dbom-channel-matrix__game-cell">
        <RenderImg
          style={{ width: 24, height: 24, borderRadius: 4, flexShrink: 0 }}
          coverImage={game?.cover || ''}
          defaultImage={DefaultImg.GAME}
        />
        <TextBubbles
          text={game?.game_name || row.game_id}
          className="dbom-channel-matrix__game-name"
        />
      </div>
    );
  };

  /**
   * Channel 列头：渠道 icon + 渠道名
   * 名称优先使用 channelMap 映射，图标仍走 channelInfo
   */
  const renderChannelHeader = (channelId: string) => {
    const chName = channelMap[channelId]?.text
      || channelInfo(channelId as ChannelIconType, undefined, true).name
      || channelId;
    const { img } = channelInfo(channelId as ChannelIconType, undefined, true);
    return (
      <span
        className="dbom-channel-matrix__channel-header"
        title={chName}
      >
        <img
          className="dbom-channel-matrix__channel-icon"
          src={img}
          alt={chName}
        />
        <TextBubbles className="dbom-channel-matrix__channel-name" text={chName} />
      </span>
    );
  };

  /** 列定义 */
  const columns = useMemo(() => {
    return [
      {
        key: 'game',
        header: labels.game,
        width: 240,
        fixed: 'left' as const,
        render: renderGameCell,
      },
      ...channelIds.map(cid => ({
        key: cid,
        header: renderChannelHeader(cid),
        align: 'right' as const,
        width: 200,
        render: (row: IGameRow) => {
          const value = row.byChannel[cid] ?? 0;
          const cell = linkMap[row.game_id]?.[cid];
          /**
           * 进度条基准（totalValue）：
           * - 百分比 metric：value 已经是 0~1，直接用 1（即 100%）作 100% 基准
           *   → 「35% 条 = 35% 长」，与文本完全对齐
           *   接口的 `dimension_max` 对 percent metric 是 raw_value 单位（绝对计数），
           *   尺度不匹配，不能直接拿来当百分比的 totalValue。
           * - 绝对值 metric：用 API 的 dimension_max[cid]（该列峰值），列内对比
           */

          return (
            <ProgressColumn
              percentage={cell?.qoq ?? 0}
              value={value}
              oldValue={cell?.oldValue ?? 0}
              totalValue={dimensionMax?.[cid]}
              showProgress
              newTime={linkTime.newTime}
              oldTime={linkTime.oldTime}
              formateValue={formateValue}
              formateDate={(date: string) => DBO_TM.sFormat(date)}
            />
          );
        },
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, channelIds, gameMap, channelMap, linkMap, linkTime, dimensionMax, formateValue, isPercent]);

  /** 排序列：每个渠道 */
  const sortColumns = useMemo(() => [...channelIds], [channelIds]);
  const handleSortChange = (next: SortBy<any>) => {
    onSortChange(next);
  };

  const recordKey = useMemo(() => (r: IGameRow) => r.game_id, []);

  return (
    <TableCommonHook
      className="dbom-channel-matrix-table"
      records={empty ? [] : pagedRows}
      columns={columns}
      recordKey={recordKey}
      total={empty ? 0 : sortedRows.length}
      pageInfo={pageInfo}
      setPageInfo={setPageInfo}
      defaultPageInfo={DEFAULT_PAGE_INFO}
      pageSizeOptions={[10, 20, 50]}
      hasPageable={pagedRows.length > 50}
      sortColumns={sortColumns}
      sortType={'local'}
      sortInfo={[sort, handleSortChange]}
      changeSort={handleSortChange}
      loading={loading}
      loadingHeight={height}
      maxHeight={height}
      emptyProps={{ emptyTitleText, hasDesc: false }}
      emptyHeight={height}
      bordered={'all'}
    />
  );
}
